import React, { useState, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useHomeSection } from "../../hooks/useHomeContent";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const absolutize = (u) => {
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  return `${BACKEND_URL}${u}`;
};

const isVideoUrl = (u) => /\.(mp4|mov|webm|m4v)(?:\?|$)/i.test(u || "");

/** Render either a video (when the item has one) or the thumbnail image. */
function LookMedia({ item, autoPlay = false, className = "" }) {
  const videoSrc = item.video || (isVideoUrl(item.image) ? item.image : "");
  const posterSrc = item.poster || (!isVideoUrl(item.image) ? item.image : "") || "";
  if (videoSrc) {
    return (
      <video
        className={className}
        src={absolutize(videoSrc)}
        poster={posterSrc ? absolutize(posterSrc) : undefined}
        autoPlay={autoPlay}
        muted
        loop
        playsInline
        preload={autoPlay ? "metadata" : "none"}
      />
    );
  }
  return <img className={className} src={absolutize(item.image)} alt={item.label} />;
}

// Built-in fallbacks (used only when admin has added nothing to section="looks")
const DEFAULT_LOOKS = [
  {
    id: "look-1",
    label: "Modern Look",
    image:
      "https://images.unsplash.com/photo-1622398925373-3f91b1e275f5?auto=format&fit=crop&w=900&q=85",
    link: "/collections/diamond",
  },
  {
    id: "look-2",
    label: "Office Look",
    image:
      "https://images.unsplash.com/photo-1611591437281-460bfbe1220a?auto=format&fit=crop&w=900&q=85",
    link: "/collections/gold",
  },
  {
    id: "look-3",
    label: "Classic Look",
    image:
      "https://images.unsplash.com/photo-1605100804763-247f67b3557e?auto=format&fit=crop&w=900&q=85",
    link: "/collections/temple-antique",
  },
  {
    id: "look-4",
    label: "Bridal Look",
    image:
      "https://images.unsplash.com/photo-1601121141461-9d6647bca1ed?auto=format&fit=crop&w=900&q=85",
    link: "/collections/temple-antique",
  },
  {
    id: "look-5",
    label: "Everyday Look",
    image:
      "https://images.unsplash.com/photo-1611107683227-e9060eccd846?auto=format&fit=crop&w=900&q=85",
    link: "/collections/silver",
  },
];

export default function ChooseYourLook() {
  const custom = useHomeSection("looks");
  // when admin hasn't added anything, useHomeSection returns [] for unknown sections
  const looks = custom && custom.length ? custom : DEFAULT_LOOKS;
  const [active, setActive] = useState(0);
  const [direction, setDirection] = useState(1); // 1 = forwards, -1 = backwards
  const [paused, setPaused] = useState(false);
  const [interacted, setInteracted] = useState(false); // slow down after first touch

  const total = looks.length;
  const wrap = useCallback((i) => (i + total) % total, [total]);

  const next = useCallback(() => {
    setDirection(1);
    setActive((a) => wrap(a + 1));
  }, [wrap]);
  const prev = useCallback(() => {
    setDirection(-1);
    setActive((a) => wrap(a - 1));
  }, [wrap]);

  const markInteraction = useCallback(() => setInteracted(true), []);

  // user-triggered navigation (always slows down autoplay)
  const userNext = useCallback(() => { markInteraction(); next(); }, [markInteraction, next]);
  const userPrev = useCallback(() => { markInteraction(); prev(); }, [markInteraction, prev]);
  const userSet  = useCallback((i) => {
    markInteraction();
    setDirection(i > active ? 1 : -1);
    setActive(i);
  }, [markInteraction, active]);

  // keyboard arrows
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") { markInteraction(); next(); }
      if (e.key === "ArrowLeft")  { markInteraction(); prev(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev, markInteraction]);

  // auto-play — 5s default, slows to 8s after first interaction, hover pauses.
  // (Was 2s/4s which was too aggressive for mobile — animations couldn't finish
  // between slides, causing the "glitchy" jank the shop owner reported.)
  useEffect(() => {
    if (paused || total <= 1) return undefined;
    const delay = interacted ? 8000 : 5000;
    const id = setInterval(next, delay);
    return () => clearInterval(id);
  }, [paused, total, next, active, interacted]);

  if (total === 0) return null;

  const current = looks[active];
  const leftItem = looks[wrap(active - 1)];
  const rightItem = looks[wrap(active + 1)];

  const handleDrag = (_, info) => {
    markInteraction();
    if (info.offset.x < -60) next();
    else if (info.offset.x > 60) prev();
  };

  return (
    <section className="cyl">
      <div className="wrap">
        <motion.div
          className="cyl-head"
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true, amount: 0.3 }}
        >
          <span className="cyl-eyebrow">Choose Your Look</span>
          <h2>Trending Now.</h2>
          <p>
            Swipe through today&apos;s most-loved styles. Tap any look to see
            the piece behind the shot.
          </p>
        </motion.div>

        <div
          className="cyl-stage"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <button
            type="button"
            className="cyl-arrow cyl-arrow-l"
            onClick={userPrev}
            aria-label="Previous look"
            data-testid="cyl-prev"
          >
            <ChevronLeft size={22} />
          </button>

          {/* left peek — static wrapper; media crossfades via CSS `key` change */}
          {total > 1 && (
            <div className="cyl-slot cyl-slot-left" aria-hidden="true">
              <Link
                to={leftItem.link || "/"}
                className="cyl-side-link"
                tabIndex={-1}
                key={`left-${leftItem.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  userPrev();
                }}
              >
                <LookMedia item={leftItem} autoPlay={false} />
                <span className="cyl-label cyl-label-side">
                  {leftItem.label}
                </span>
              </Link>
            </div>
          )}

          {/* center — direction-aware slide + fade (fixed-duration tween keeps
              mobile frame timing consistent — springs varied too much on
              slower phones and looked glitchy). */}
          <AnimatePresence mode="popLayout" initial={false} custom={direction}>
            <motion.div
              key={current.id}
              className="cyl-slot cyl-slot-main"
              custom={direction}
              initial={(d) => ({ opacity: 0, x: d > 0 ? 80 : -80 })}
              animate={{ opacity: 1, x: 0 }}
              exit={(d) => ({ opacity: 0, x: d > 0 ? -80 : 80 })}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.2}
              onDragEnd={handleDrag}
              data-testid={`cyl-active-${(current.label || "").toLowerCase().replace(/\s+/g, "-")}`}
            >
              <Link
                to={current.link || "/"}
                className="cyl-main-link"
                data-testid="cyl-active-link"
              >
              <LookMedia item={current} autoPlay={true} />
                <div className="cyl-main-overlay" />
              </Link>
            </motion.div>
          </AnimatePresence>

          {/* right peek — static wrapper, matches left */}
          {total > 1 && (
            <div className="cyl-slot cyl-slot-right" aria-hidden="true">
              <Link
                to={rightItem.link || "/"}
                className="cyl-side-link"
                tabIndex={-1}
                key={`right-${rightItem.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  userNext();
                }}
              >
                <LookMedia item={rightItem} autoPlay={false} />
                <span className="cyl-label cyl-label-side">
                  {rightItem.label}
                </span>
              </Link>
            </div>
          )}

          <button
            type="button"
            className="cyl-arrow cyl-arrow-r"
            onClick={userNext}
            aria-label="Next look"
            data-testid="cyl-next"
          >
            <ChevronRight size={22} />
          </button>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={`lbl-${current.id}`}
            className="cyl-active-label"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            {current.label}
          </motion.div>
        </AnimatePresence>

        <div className="cyl-dots" role="tablist">
          {looks.map((l, i) => (
            <button
              key={l.id || i}
              type="button"
              role="tab"
              aria-selected={i === active}
              aria-label={`Show ${l.label}`}
              className={`cyl-dot ${i === active ? "on" : ""}`}
              onClick={() => userSet(i)}
              data-testid={`cyl-dot-${i}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
